<template>
    <v-container fluid>
        <!-- Edit Plant Dialog -->
        <PlantEditDialog
            :dialog.sync="editPlantDialog"
            :object.sync="plant"
        ></PlantEditDialog>

        <!-- Plant Growth Template Dialog -->
        <PlantGrowthTemplateEditDialog
            :dialog.sync="editPlantGrowthTemplateDialog"
            :plant="plant"
            :object.sync="editedPlantGrowthTemplate"
            :defaultPotSizeNodeId.sync="selectedPotSizeNodeId"
            :isCopy="isCopyGrowthTemplate"
            v-on:item-created="plantGrowthTemplateAdded"
            v-on:item-updated="plantGrowthTemplateEdited"
        ></PlantGrowthTemplateEditDialog>

        <!-- Growth Cost Template Dialog -->
        <PlantFixedCostTemplateEditDialog
            :dialog.sync="editPlantFixedCostTemplateDialog"
            :defaultPotSizeNodeId.sync="selectedPotSizeNodeId"
            v-on:item-created="onPlantFixedCostTemplateCreated"
        ></PlantFixedCostTemplateEditDialog>

        <v-dialog
            v-model="productSalesPriceChartDialog"
            class="pa-2"
            @click:outside="closeProductSalesPriceChart"
        >
            <!-- product sales price chart -->
            <ProductSalesPriceChart ref="productSalesPriceChartRef">
                <template v-slot:header>
                    <v-card-title>
                        <v-icon color="primary" class="mr-2">trending_up</v-icon
                        >Product Sales Price By Week
                        <v-spacer></v-spacer>
                        <v-btn icon @click="closeProductSalesPriceChart">
                            <v-icon color="primary">close</v-icon>
                        </v-btn>
                    </v-card-title>
                </template>
            </ProductSalesPriceChart>
        </v-dialog>

        <PlantTemplateGrowthTaskScoreListDialog
            :dialog.sync="plantTemplateGrowthTaskScoreListDialog"
            :plantGrowthTemplateObj.sync="selectedPlantGrowthTemplate"
        >
        </PlantTemplateGrowthTaskScoreListDialog>

        <!-- Delete Plant Dialog -->
        <DeleteDialog
            :dialog.sync="deletePlantDialog"
            :item="plant"
            v-on:confirmed="deletePlantConfirmed"
        >
            <template v-slot:title>Delete - {{ plant.name }}</template>
            <template v-slot:content
                >Are you sure you want to delete this plant?</template
            >
        </DeleteDialog>

        <!-- Delete Plant Growth Template Dialog -->
        <DeleteDialog
            :dialog.sync="deletePlantGrowthTemplateDialog"
            :item="deletedPlantGrowthTemplate"
            v-on:confirmed="deletePlantGrowthTemplateConfirmed"
        >
            <template v-slot:title
                >Delete -
                {{
                    deletedPlantGrowthTemplate
                        ? deletedPlantGrowthTemplate.name
                        : ""
                }}</template
            >
            <template v-slot:content
                >Are you sure you want to delete this growth template?</template
            >
        </DeleteDialog>

        <v-row class="mx-0">
            <!-- Plant Detail Card-->
            <v-col cols="12" lg="3" class="px-2">
                <PlantInfoCard
                    :plant="plant"
                    :loading="$apollo.queries.plant.loading"
                >
                    <template v-slot:header>
                        <v-card-title>
                            <v-icon color="primary" class="mr-2">eco</v-icon
                            >Plant
                            <v-spacer></v-spacer>

                            <v-btn
                                v-if="hasPermission('change_plant')"
                                class="mx-2"
                                color="primary"
                                outlined
                                dark
                                fab
                                x-small
                                @click="editPlantDialog = true"
                            >
                                <v-icon>edit</v-icon>
                            </v-btn>
                            <v-btn
                                v-if="hasPermission('delete_plant')"
                                color="error"
                                outlined
                                dark
                                fab
                                x-small
                                @click="deletePlantDialog = true"
                            >
                                <v-icon>delete</v-icon>
                            </v-btn>
                        </v-card-title>
                    </template>
                </PlantInfoCard>
            </v-col>

            <!-- Right  -->
            <v-col v-if="plant.id" cols="12" lg="9" class="px-2">
                <!-- For each potsize -->
                <v-row
                    class="ma-0"
                    v-for="(plantPotSize, index) in plantPotSizes"
                    :key="index"
                >
                    <v-toolbar flat>
                        <h1 class="mr-3" v-if="plantPotSize.node.potSize.id">
                            {{ plantPotSize.node.potSize.potSizeCm || "-" }} cm
                        </h1>
                        <v-spacer></v-spacer>
                    </v-toolbar>

                    <!-- expansion-panels -->
                    <v-col cols="12" class="mx-0 px-0 pt-0">
                        <v-hover>
                            <template v-slot="{ hover }">
                                <v-card
                                    outlined
                                    :elevation="hover ? 6 : 1"
                                    :loading="$apollo.queries.plant.loading"
                                >
                                    <v-expansion-panels class="grey lighten-4">
                                        <!-- plant growth templates -->
                                        <v-expansion-panel
                                            :set="
                                                (currentPlantGrowthTemplates = plantGrowthTemplates.filter(
                                                    item => {
                                                        return (
                                                            item.node.potSize
                                                                .id ==
                                                            plantPotSize.node
                                                                .potSize.id
                                                        );
                                                    }
                                                ))
                                            "
                                        >
                                            <v-expansion-panel-header
                                                class="py-0 pl-0"
                                            >
                                                <v-card-title
                                                    class="subtitle-1"
                                                >
                                                    <v-icon
                                                        color="primary"
                                                        class="mr-2"
                                                        >event_note</v-icon
                                                    >Growth Templates
                                                    <span
                                                        v-if="
                                                            currentPlantGrowthTemplates.length >
                                                                0
                                                        "
                                                        class="ml-3 grey--text"
                                                        >{{
                                                            currentPlantGrowthTemplates.length
                                                        }}</span
                                                    >
                                                    <v-icon
                                                        v-else-if="
                                                            plantPotSize.node
                                                                .potSize.id
                                                        "
                                                        small
                                                        class="ml-3 warning--text"
                                                    >
                                                        warning
                                                    </v-icon>
                                                </v-card-title>
                                                <v-spacer></v-spacer>
                                            </v-expansion-panel-header>
                                            <v-expansion-panel-content
                                                class="mx-n2"
                                            >
                                                <v-divider></v-divider>
                                                <v-data-table
                                                    :headers="
                                                        plantGrowthTemplateTableHeaders
                                                    "
                                                    :items="
                                                        plantGrowthTemplates.filter(
                                                            item => {
                                                                return (
                                                                    item.node
                                                                        .potSize
                                                                        .id ==
                                                                    plantPotSize
                                                                        .node
                                                                        .potSize
                                                                        .id
                                                                );
                                                            }
                                                        )
                                                    "
                                                    :loading="
                                                        $apollo.queries.plant
                                                            .loading
                                                    "
                                                    item-key="node.id"
                                                    disable-pagination
                                                    hide-default-footer
                                                    class="pa-3"
                                                >
                                                    <template
                                                        v-slot:item.potSize="{
                                                            item
                                                        }"
                                                    >
                                                        <v-chip
                                                            class="primary dark"
                                                            >{{
                                                                item.node
                                                                    .potSize
                                                                    .potSizeCm
                                                            }}
                                                            cm</v-chip
                                                        >
                                                    </template>
                                                    <template
                                                        v-slot:item.node.seasonEffectMonthList="{
                                                            item
                                                        }"
                                                    >
                                                        <v-chip-group
                                                            v-model="
                                                                item.node
                                                                    .seasonEffectMonthList
                                                            "
                                                            active-class="primary dark"
                                                            multiple
                                                            column
                                                        >
                                                            <v-chip
                                                                label
                                                                v-for="month in monthsChoices"
                                                                :key="month"
                                                                :value="month"
                                                                >{{
                                                                    month
                                                                }}</v-chip
                                                            >
                                                        </v-chip-group>
                                                    </template>
                                                    <template
                                                        v-slot:item.node.avgGrowthDays="{
                                                            item
                                                        }"
                                                    >
                                                        <v-chip
                                                            v-if="
                                                                item.node
                                                                    .avgGrowthDays !=
                                                                    null
                                                            "
                                                            :color="
                                                                Math.abs(
                                                                    item.node
                                                                        .avgGrowthDays -
                                                                        item
                                                                            .node
                                                                            .totalGrowthDays
                                                                ) < 3
                                                                    ? 'success'
                                                                    : Math.abs(
                                                                          item
                                                                              .node
                                                                              .avgGrowthDays -
                                                                              item
                                                                                  .node
                                                                                  .totalGrowthDays
                                                                      ) < 8
                                                                    ? 'warning'
                                                                    : 'error'
                                                            "
                                                            dark
                                                            small
                                                            @click="
                                                                showPlantTemplateGrowthTaskScoreListDialog(
                                                                    item.node
                                                                )
                                                            "
                                                        >
                                                            <span
                                                                v-if="
                                                                    item.node
                                                                        .avgGrowthDays -
                                                                        item
                                                                            .node
                                                                            .totalGrowthDays >
                                                                        0
                                                                "
                                                                >+</span
                                                            >
                                                            {{
                                                                item.node
                                                                    .avgGrowthDays -
                                                                    item.node
                                                                        .totalGrowthDays
                                                            }}
                                                        </v-chip>
                                                    </template>
                                                    <template
                                                        v-slot:item.node.avgSalesDays="{
                                                            item
                                                        }"
                                                    >
                                                        <v-chip
                                                            v-if="
                                                                item.node
                                                                    .avgSalesDays !=
                                                                    null
                                                            "
                                                            :color="
                                                                Math.abs(
                                                                    item.node
                                                                        .avgSalesDays -
                                                                        item
                                                                            .node
                                                                            .maxSalesDays
                                                                ) < 3
                                                                    ? 'success'
                                                                    : Math.abs(
                                                                          item
                                                                              .node
                                                                              .avgSalesDays -
                                                                              item
                                                                                  .node
                                                                                  .maxSalesDays
                                                                      ) < 8
                                                                    ? 'warning'
                                                                    : 'error'
                                                            "
                                                            dark
                                                            small
                                                            @click="
                                                                showPlantTemplateGrowthTaskScoreListDialog(
                                                                    item.node
                                                                )
                                                            "
                                                        >
                                                            <span
                                                                v-if="
                                                                    item.node
                                                                        .avgSalesDays -
                                                                        item
                                                                            .node
                                                                            .maxSalesDays >
                                                                        0
                                                                "
                                                                >+</span
                                                            >
                                                            {{
                                                                item.node
                                                                    .avgSalesDays -
                                                                    item.node
                                                                        .maxSalesDays
                                                            }}
                                                        </v-chip>
                                                    </template>
                                                    <template
                                                        v-slot:item.action="{
                                                            item
                                                        }"
                                                    >
                                                        <v-row
                                                            class="justify-center"
                                                        >
                                                            <v-btn
                                                                v-if="
                                                                    hasPermission(
                                                                        'add_plantgrowthtemplate'
                                                                    )
                                                                "
                                                                color="grey darken-2"
                                                                outlined
                                                                dark
                                                                fab
                                                                x-small
                                                                class="ml-1"
                                                                @click="
                                                                    copyPlantGrowthTemplate(
                                                                        item
                                                                    )
                                                                "
                                                            >
                                                                <v-icon
                                                                    >file_copy</v-icon
                                                                >
                                                            </v-btn>
                                                            <v-btn
                                                                v-if="
                                                                    hasPermission(
                                                                        'change_plantgrowthtemplate'
                                                                    )
                                                                "
                                                                color="grey darken-2"
                                                                outlined
                                                                dark
                                                                fab
                                                                x-small
                                                                class="ml-1"
                                                                @click="
                                                                    editPlantGrowthTemplate(
                                                                        item
                                                                    )
                                                                "
                                                            >
                                                                <v-icon
                                                                    >edit</v-icon
                                                                >
                                                            </v-btn>
                                                            <v-btn
                                                                v-if="
                                                                    hasPermission(
                                                                        'delete_plantgrowthtemplate'
                                                                    )
                                                                "
                                                                color="error"
                                                                outlined
                                                                dark
                                                                fab
                                                                x-small
                                                                class="ml-1"
                                                                @click="
                                                                    deletePlantGrowthTemplate(
                                                                        item
                                                                    )
                                                                "
                                                            >
                                                                <v-icon
                                                                    >delete</v-icon
                                                                >
                                                            </v-btn>
                                                        </v-row>
                                                    </template>
                                                </v-data-table>
                                                <v-btn
                                                    v-if="
                                                        hasPermission(
                                                            'add_plantgrowthtemplate'
                                                        )
                                                    "
                                                    class="ml-3"
                                                    color="primary"
                                                    outlined
                                                    rounded
                                                    small
                                                    :disabled="
                                                        $apollo.queries.plant
                                                            .loading
                                                    "
                                                    @click="
                                                        addPlantGrowthTemplate(
                                                            plantPotSize.node
                                                                .potSize.id
                                                        )
                                                    "
                                                    >+ New Template</v-btn
                                                >
                                            </v-expansion-panel-content>
                                        </v-expansion-panel>

                                        <!-- growth cost -->
                                        <v-expansion-panel
                                            :set="
                                                (currentPlantFixedCostTemplateItems =
                                                    plantPotSize.node
                                                        .plantFixedCostTemplate &&
                                                    plantPotSize.node
                                                        .plantFixedCostTemplate
                                                        .plantFixedCostTemplateItemSet
                                                        ? plantPotSize.node
                                                              .plantFixedCostTemplate
                                                              .plantFixedCostTemplateItemSet
                                                              .edges
                                                        : [])
                                            "
                                        >
                                            <v-expansion-panel-header
                                                class="py-0 pl-0"
                                            >
                                                <v-card-title
                                                    class="subtitle-1"
                                                >
                                                    <v-icon
                                                        color="primary"
                                                        class="mr-2"
                                                        >euro_symbol</v-icon
                                                    >Fixed Costs
                                                    <span
                                                        v-if="
                                                            currentPlantFixedCostTemplateItems.length >
                                                                0
                                                        "
                                                        class="ml-3 grey--text"
                                                        >€{{
                                                            (
                                                                (plantPotSize
                                                                    .node
                                                                    .youngPlantDefaultCost ||
                                                                    0) +
                                                                (plantPotSize
                                                                    .node
                                                                    .plantFixedCostTemplate
                                                                    ? plantPotSize
                                                                          .node
                                                                          .plantFixedCostTemplate
                                                                          .totalCost
                                                                    : 0)
                                                            ).toFixed(3)
                                                        }}</span
                                                    >
                                                    <v-icon
                                                        v-else-if="
                                                            plantPotSize.node
                                                                .potSize.id
                                                        "
                                                        small
                                                        class="ml-3 warning--text"
                                                    >
                                                        warning
                                                    </v-icon>
                                                </v-card-title>
                                                <v-spacer></v-spacer>
                                            </v-expansion-panel-header>
                                            <v-expansion-panel-content
                                                class="mx-n2"
                                            >
                                                <v-divider></v-divider>
                                                <v-progress-linear
                                                    :active="
                                                        isSavingYoungPlantDefaultCost
                                                    "
                                                    :indeterminate="
                                                        isSavingYoungPlantDefaultCost
                                                    "
                                                    absolute
                                                    top
                                                    height="2"
                                                ></v-progress-linear>
                                                <v-row
                                                    dense
                                                    class="mx-0 align-center"
                                                >
                                                    <v-col cols="12" md="4">
                                                        <v-card-title
                                                            class="subtitle-1"
                                                        >
                                                            Young plant cost
                                                        </v-card-title>
                                                    </v-col>
                                                    <!-- young plant default cost -->
                                                    <v-col
                                                        cols="12"
                                                        md="4"
                                                        class="pl-3"
                                                    >
                                                        <v-edit-dialog
                                                            :key="
                                                                plantPotSize
                                                                    .node.id
                                                            "
                                                            @open="
                                                                editingYoungPlantDefaultCost =
                                                                    plantPotSize
                                                                        .node
                                                                        .youngPlantDefaultCost
                                                            "
                                                            @save="
                                                                savePlantPotSizeYoungPlantDefaultCost(
                                                                    plantPotSize
                                                                        .node
                                                                        .id,
                                                                    editingYoungPlantDefaultCost
                                                                )
                                                            "
                                                        >
                                                            <span
                                                                class="headline primary--text"
                                                                >€{{
                                                                    plantPotSize
                                                                        .node
                                                                        .youngPlantDefaultCost ||
                                                                        0
                                                                }}</span
                                                            >
                                                            <template
                                                                v-slot:input
                                                            >
                                                                <v-text-field
                                                                    v-model="
                                                                        editingYoungPlantDefaultCost
                                                                    "
                                                                    :rules="[
                                                                        rules.required
                                                                    ]"
                                                                    prefix="€"
                                                                    label="Edit"
                                                                    type="number"
                                                                    single-line
                                                                ></v-text-field>
                                                            </template>
                                                        </v-edit-dialog>
                                                    </v-col>
                                                </v-row>

                                                <v-row
                                                    dense
                                                    class="mt-5 mx-0 align-start"
                                                >
                                                    <v-col cols="12" md="4">
                                                        <v-card-title
                                                            class="subtitle-1"
                                                        >
                                                            Growth costs
                                                        </v-card-title>
                                                    </v-col>
                                                    <v-col
                                                        cols="12"
                                                        md="8"
                                                        class="px-3"
                                                    >
                                                        <!-- growth cost template -->
                                                        <v-autocomplete
                                                            hide-details
                                                            v-model="
                                                                plantPotSize
                                                                    .node
                                                                    .plantFixedCostTemplateNodeId
                                                            "
                                                            :items="
                                                                tenantPlantFixedCostTemplates
                                                                    ? tenantPlantFixedCostTemplates.filter(
                                                                          item => {
                                                                              return (
                                                                                  item
                                                                                      .node
                                                                                      .potSize
                                                                                      .id ==
                                                                                  plantPotSize
                                                                                      .node
                                                                                      .potSize
                                                                                      .id
                                                                              );
                                                                          }
                                                                      )
                                                                    : []
                                                            "
                                                            item-text="node.templateName"
                                                            item-value="node.id"
                                                            :loading="
                                                                $apollo.queries
                                                                    .tenantPlantFixedCostTemplates
                                                                    .loading ||
                                                                    isLoadingTenantPlantFixedCostTemplates
                                                            "
                                                            label="Select Growth Cost Template"
                                                            class="subtitle-2"
                                                            :change="
                                                                linkPlantFixedCostTemplate(
                                                                    plantPotSize.node
                                                                )
                                                            "
                                                            outlined
                                                        >
                                                            <template
                                                                slot="item"
                                                                slot-scope="{
                                                                    item
                                                                }"
                                                            >
                                                                <v-chip
                                                                    color="primary"
                                                                    class="mr-2"
                                                                    small
                                                                >
                                                                    {{
                                                                        item
                                                                            .node
                                                                            .potSize
                                                                            .potSizeCm
                                                                    }}cm
                                                                </v-chip>
                                                                {{
                                                                    item.node
                                                                        .templateName
                                                                }}
                                                                <span
                                                                    class="ml-2 grey--text"
                                                                >
                                                                    €{{
                                                                        item.node.totalCost.toFixed(
                                                                            3
                                                                        )
                                                                    }}
                                                                </span>
                                                            </template>
                                                            <template
                                                                slot="selection"
                                                                slot-scope="{
                                                                    item
                                                                }"
                                                            >
                                                                <v-chip
                                                                    color="primary"
                                                                    class="mr-2"
                                                                    small
                                                                >
                                                                    {{
                                                                        item
                                                                            .node
                                                                            .potSize
                                                                            .potSizeCm
                                                                    }}cm
                                                                </v-chip>
                                                                {{
                                                                    item.node
                                                                        .templateName
                                                                }}
                                                                <span
                                                                    class="ml-2 grey--text"
                                                                >
                                                                    €{{
                                                                        item.node.totalCost.toFixed(
                                                                            3
                                                                        )
                                                                    }}
                                                                </span>
                                                            </template>
                                                            <template
                                                                v-if="
                                                                    hasPermission(
                                                                        'change_plant'
                                                                    )
                                                                "
                                                                v-slot:append-outer
                                                            >
                                                                <v-btn
                                                                    text
                                                                    small
                                                                    tabindex="-1"
                                                                    @click="
                                                                        addPlantFixedCostTemplate(
                                                                            plantPotSize
                                                                                .node
                                                                                .potSize
                                                                                .id
                                                                        )
                                                                    "
                                                                >
                                                                    <v-icon
                                                                        color="grey"
                                                                        tabindex="-1"
                                                                        >add</v-icon
                                                                    >
                                                                </v-btn>
                                                            </template>
                                                        </v-autocomplete>

                                                        <v-data-table
                                                            :headers="
                                                                plantFixedCostTemplateItemTableHeaders
                                                            "
                                                            :items="
                                                                currentPlantFixedCostTemplateItems
                                                            "
                                                            :loading="
                                                                $apollo.queries
                                                                    .plant
                                                                    .loading ||
                                                                    isLoadingTenantPlantFixedCostTemplates
                                                            "
                                                            item-key="node.id"
                                                            disable-pagination
                                                            hide-default-footer
                                                            class="pa-3"
                                                        >
                                                            <template
                                                                v-slot:item.node.costValue="{
                                                                    item
                                                                }"
                                                            >
                                                                €{{
                                                                    item.node
                                                                        .costValue
                                                                }}
                                                            </template>

                                                            <!-- table footer-->
                                                            <template
                                                                slot="body.append"
                                                            >
                                                                <!-- failure % -->
                                                                <tr>
                                                                    <th
                                                                        class="body-2"
                                                                    >
                                                                        Failure
                                                                        (%)
                                                                    </th>
                                                                    <th
                                                                        class="body-2"
                                                                    >
                                                                        {{
                                                                            plantPotSize
                                                                                .node
                                                                                .plantFixedCostTemplate
                                                                                ? plantPotSize
                                                                                      .node
                                                                                      .plantFixedCostTemplate
                                                                                      .failureRate *
                                                                                  100
                                                                                : 0
                                                                        }}%
                                                                    </th>
                                                                </tr>

                                                                <!-- subtotal -->
                                                                <tr>
                                                                    <th
                                                                        class="subtitle-2"
                                                                    >
                                                                        Subtotal
                                                                    </th>
                                                                    <th
                                                                        class="subtitle-2"
                                                                    >
                                                                        €{{
                                                                            plantPotSize
                                                                                .node
                                                                                .plantFixedCostTemplate
                                                                                ? plantPotSize.node.plantFixedCostTemplate.totalCost.toFixed(
                                                                                      3
                                                                                  )
                                                                                : 0
                                                                        }}
                                                                    </th>
                                                                </tr>
                                                            </template>
                                                        </v-data-table>
                                                    </v-col>
                                                </v-row>

                                                <!-- Total cost -->
                                                <v-row
                                                    dense
                                                    class="mt-5 mx-0 align-center"
                                                >
                                                    <v-col cols="12" md="4">
                                                        <v-card-title>
                                                            Total cost
                                                        </v-card-title>
                                                    </v-col>
                                                    <v-col
                                                        cols="12"
                                                        md="8"
                                                        class="px-3 headline"
                                                        >€{{
                                                            (
                                                                (plantPotSize
                                                                    .node
                                                                    .youngPlantDefaultCost ||
                                                                    0) +
                                                                (plantPotSize
                                                                    .node
                                                                    .plantFixedCostTemplate
                                                                    ? plantPotSize
                                                                          .node
                                                                          .plantFixedCostTemplate
                                                                          .totalCost
                                                                    : 0)
                                                            ).toFixed(3)
                                                        }}
                                                    </v-col>
                                                </v-row>
                                            </v-expansion-panel-content>
                                        </v-expansion-panel>

                                        <!-- plant products -->
                                        <v-expansion-panel
                                            :set="
                                                (currentPlantProducts = plantProducts.filter(
                                                    item => {
                                                        return (
                                                            item.node.potSize
                                                                .id ==
                                                            plantPotSize.node
                                                                .potSize.id
                                                        );
                                                    }
                                                ))
                                            "
                                        >
                                            <v-expansion-panel-header
                                                class="py-0 pl-0"
                                            >
                                                <v-card-title
                                                    class="subtitle-1"
                                                >
                                                    <v-icon
                                                        color="primary"
                                                        class="mr-2"
                                                        >local_florist</v-icon
                                                    >Products
                                                    <span
                                                        v-if="
                                                            plantPotSize.node
                                                                .potSize.id
                                                        "
                                                        class="ml-3 grey--text"
                                                        >{{
                                                            currentPlantProducts.length
                                                        }}</span
                                                    >
                                                </v-card-title>
                                                <v-spacer></v-spacer>
                                            </v-expansion-panel-header>
                                            <v-expansion-panel-content
                                                class="mx-n2"
                                            >
                                                <v-divider></v-divider>
                                                <v-data-table
                                                    :headers="
                                                        productTableHeaders
                                                    "
                                                    :items="
                                                        currentPlantProducts
                                                    "
                                                    item-key="node.id"
                                                    disable-pagination
                                                    :loading="
                                                        $apollo.queries.plant
                                                            .loading
                                                    "
                                                    hide-default-footer
                                                    sort-by="node.product.name"
                                                    class=""
                                                >
                                                    <template
                                                        v-slot:item.node.product.isDynamic="{
                                                            item
                                                        }"
                                                    >
                                                        <v-chip
                                                            small
                                                            color="primary"
                                                            v-if="
                                                                item.node
                                                                    .product
                                                                    .isDynamic
                                                            "
                                                            >Dynamic</v-chip
                                                        >
                                                        <span
                                                            v-else
                                                            class="grey--text"
                                                            >NO</span
                                                        >
                                                    </template>
                                                    <template
                                                        v-slot:item.node.product.productFixedCostTemplate.totalCost="{
                                                            item
                                                        }"
                                                    >
                                                        <span
                                                            v-if="
                                                                item.node
                                                                    .product
                                                                    .productFixedCostTemplate
                                                            "
                                                        >
                                                            €{{
                                                                item.node
                                                                    .product
                                                                    .productFixedCostTemplate
                                                                    ? item.node
                                                                          .product
                                                                          .productFixedCostTemplate
                                                                          .totalCost
                                                                    : 0
                                                            }}
                                                        </span>
                                                        <span v-else>
                                                            -
                                                        </span>
                                                    </template>
                                                    <template
                                                        v-slot:item.salesPrice="{
                                                            item
                                                        }"
                                                    >
                                                        <v-btn
                                                            icon
                                                            color="primary"
                                                            @click="
                                                                showProductSalesPriceChart(
                                                                    item.node
                                                                        .product
                                                                        .id
                                                                )
                                                            "
                                                        >
                                                            <v-icon
                                                                >visibility</v-icon
                                                            >
                                                        </v-btn>
                                                    </template>
                                                    <template
                                                        v-slot:item.action="{
                                                            item
                                                        }"
                                                    >
                                                        <v-btn
                                                            icon
                                                            color="primary"
                                                            @click="
                                                                goToProductDetail(
                                                                    item.node
                                                                        .product
                                                                        .id
                                                                )
                                                            "
                                                        >
                                                            <v-icon
                                                                >launch</v-icon
                                                            >
                                                        </v-btn>
                                                    </template>
                                                </v-data-table>
                                            </v-expansion-panel-content>
                                        </v-expansion-panel>
                                    </v-expansion-panels>
                                </v-card>
                            </template>
                        </v-hover>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import gql from "graphql-tag";
import helper from "@/utils/helper.js";
import PlantInfoCard from "@/components/plants/PlantInfoCard.vue";
import PlantEditDialog from "@/components/plants/PlantEditDialog.vue";
import PlantGrowthTemplateEditDialog from "@/components/plants/PlantGrowthTemplateEditDialog.vue";
import PlantFixedCostTemplateEditDialog from "@/components/cost/PlantFixedCostTemplateEditDialog.vue";
import DeleteDialog from "@/components/base/DeleteDialog.vue";
import ProductSalesPriceChart from "@/components/products/ProductSalesPriceChart.vue";
import PlantTemplateGrowthTaskScoreListDialog from "@/components/plants/PlantTemplateGrowthTaskScoreListDialog.vue";

export default {
    name: "plant-detail-component",
    props: ["plantNodeId"],
    components: {
        PlantInfoCard,
        PlantEditDialog,
        PlantGrowthTemplateEditDialog,
        PlantFixedCostTemplateEditDialog,
        DeleteDialog,
        ProductSalesPriceChart,
        PlantTemplateGrowthTaskScoreListDialog
    },

    apollo: {
        plant: {
            query: gql`
                query plant($id: ID!) {
                    plant(id: $id) {
                        id
                        name
                        plantSpecies {
                            id
                            name
                        }
                        defaultSupplier {
                            id
                            name
                        }
                        plantColorSet {
                            edges {
                                node {
                                    id
                                    color {
                                        id
                                        color
                                    }
                                }
                            }
                        }
                        plantPotSizeSet {
                            edges {
                                node {
                                    id
                                    youngPlantDefaultCost
                                    potSize {
                                        id
                                        potSizeCm
                                    }
                                    plantFixedCostTemplate {
                                        id
                                        templateName
                                        totalCost
                                        failureRate
                                        plantFixedCostTemplateItemSet(
                                            orderBy: "itemName"
                                        ) {
                                            totalCount
                                            edges {
                                                node {
                                                    id
                                                    itemName
                                                    costValue
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                        plantEnvironmentSet {
                            edges {
                                node {
                                    id
                                    environment {
                                        id
                                        environment
                                    }
                                }
                            }
                        }
                        plantTagSet {
                            edges {
                                node {
                                    id
                                    tag {
                                        id
                                        tag
                                        orderId
                                    }
                                }
                            }
                        }
                        plantGrowthTemplateSet {
                            edges {
                                node {
                                    id
                                    name
                                    numPhases
                                    totalGrowthDays
                                    maxSalesDays
                                    seasonEffectMonths
                                    totalM2Days
                                    potSize {
                                        id
                                        potSizeCm
                                    }
                                    avgGrowthDays
                                    avgSalesDays
                                    countGrowthTasks
                                }
                            }
                        }
                        productItemSet {
                            edges {
                                node {
                                    id
                                    potSize {
                                        id
                                        potSizeCm
                                    }
                                    product {
                                        id
                                        name
                                        isDynamic
                                        productFixedCostTemplate {
                                            id
                                            totalCost
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            `,
            variables() {
                return {
                    id: this.plantNodeId
                };
            },
            // Additional options here
            fetchPolicy: "cache-and-network",
            update(data) {
                //send data to parent component
                this.$emit("item-fetched", data.plant);

                if (!data.plant) {
                    alert("Unknown plant!");
                }

                var _plant = { ...data.plant };

                // TODO: inject plantPotSize.node.plantFixedCostTemplateNodeId
                _plant.plantPotSizeSet.edges = [
                    ..._plant.plantPotSizeSet.edges.map(item => {
                        item.node.plantFixedCostTemplateNodeId = item.node
                            .plantFixedCostTemplate
                            ? item.node.plantFixedCostTemplate.id
                            : null;
                        return item;
                    })
                ];

                //return result to current componet
                return _plant;
                // return JSON.parse(JSON.stringify(data.product));
            }
            // pollInterval: 60000 // ms
        },
        tenantPlantFixedCostTemplates: {
            query: gql`
                query tenantPlantFixedCostTemplates {
                    tenantPlantFixedCostTemplates {
                        edges {
                            node {
                                id
                                templateName
                                totalCost
                                failureRate
                                potSize {
                                    id
                                    potSizeCm
                                }
                                plantFixedCostTemplateItemSet(
                                    orderBy: "itemName"
                                ) {
                                    totalCount
                                    edges {
                                        node {
                                            id
                                            itemName
                                            costValue
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            `,
            variables() {
                return {};
            },
            // Additional options here
            fetchPolicy: "cache-and-network",
            update: data => {
                return data.tenantPlantFixedCostTemplates.edges.sort((a, b) => {
                    // first sort by potsize, then sort by template name
                    if (a.node.potSize.potSizeCm == b.node.potSize.potSizeCm) {
                        return a.node.templateName.toLowerCase() ==
                            b.node.templateName.toLowerCase()
                            ? 0
                            : a.node.templateName.toLowerCase() >
                              b.node.templateName.toLowerCase()
                            ? 1
                            : -1;
                    } else {
                        return a.node.potSize.potSizeCm ==
                            b.node.potSize.potSizeCm
                            ? 0
                            : a.node.potSize.potSizeCm >
                              b.node.potSize.potSizeCm
                            ? 1
                            : -1;
                    }
                });
            },
            skip: false
            // pollInterval: 60000 // ms
        }
    },

    data() {
        return {
            // isLoadingPlant: false,
            editPlantDialog: false,
            deletePlantDialog: false,
            selectPlantFixedCostTemplateDialog: false,
            editPlantFixedCostTemplateDialog: false,
            isLoadingTenantPlantFixedCostTemplates: false,
            productSalesPriceChartDialog: false,

            editPlantGrowthTemplateDialog: false,
            deletePlantGrowthTemplateDialog: false,
            isCopyGrowthTemplate: false,
            editedPlantGrowthTemplate: null,
            deletedPlantGrowthTemplate: null,
            selectedPotSizeNodeId: null,

            plantTemplateGrowthTaskScoreListDialog: false,
            selectedPlantGrowthTemplate: null,

            isSavingYoungPlantDefaultCost: false,
            editingYoungPlantDefaultCost: null,

            monthsChoices: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],

            plant: {
                id: null,
                name: "",
                plantSpecies: {
                    id: null,
                    name: ""
                },
                plantColorSet: {
                    edges: [
                        { node: { id: null, color: { id: null, color: "" } } }
                    ]
                },
                plantPotSizeSet: {
                    edges: [
                        {
                            node: {
                                id: null,
                                potSize: { id: null, potSizeCm: "" }
                            }
                        }
                    ]
                },
                plantEnvironmentSet: {
                    edges: [
                        {
                            node: {
                                id: null,
                                environment: { id: null, environment: "" }
                            }
                        }
                    ]
                },
                plantTagSet: {
                    edges: [{ node: { id: null, tag: { id: null, tag: "" } } }]
                },
                defaultSupplier: {
                    id: null,
                    name: ""
                },
                defaultGreenhouseDepartment: {
                    id: null,
                    name: "",
                    greenhouse: {
                        id: null,
                        name: ""
                    }
                },
                plantGrowthTemplateSet: [
                    { id: null, name: null, numPhases: 1, totalDays: 0 }
                ],
                productItemSet: []
            },

            plantGrowthTemplateTableHeaders: [
                // { text: "Pot Size", value: "potSize", sortable: false },
                { text: "Label", value: "node.name" },
                {
                    text: "Months",
                    value: "node.seasonEffectMonthList",
                    sortable: true
                },
                { text: "#Phases", value: "node.numPhases" },
                { text: "#Growth Days", value: "node.totalGrowthDays" },
                { text: "#Sales Days", value: "node.maxSalesDays" },
                { text: "Growth Score", value: "node.avgGrowthDays" },
                { text: "Sales Score", value: "node.avgSalesDays" },
                {
                    text: "Actions",
                    value: "action",
                    width: "120px",
                    align: "center",
                    sortable: false
                }
            ],

            plantFixedCostTemplateItemTableHeaders: [
                { text: "Cost Name", value: "node.itemName" },
                { text: "Cost Value", value: "node.costValue" }
            ],

            productTableHeaders: [
                {
                    text: "Product",
                    value: "node.product.name",
                    sortable: true
                },
                // {
                //     text: "Pot Size",
                //     value: "node.potSize.potSizeCm",
                //     sortable: true
                // },
                {
                    text: "Is Dynamic",
                    value: "node.product.isDynamic",
                    sortable: true
                },
                {
                    text: "Product Cost",
                    value: "node.product.productFixedCostTemplate.totalCost",
                    sortable: true
                },
                {
                    text: "Sales Price",
                    value: "salesPrice",
                    align: "center",
                    sortable: false
                },
                {
                    text: "Actions",
                    value: "action",
                    width: "120px",
                    align: "center",
                    sortable: false
                }
            ],
            rules: {
                required: v => !helper.isEmpty(v) || "This field is required",
                listRequired: v =>
                    (v || "").length >= 1 || `This field is required`,
                minLength: len => v =>
                    (v || "").length >= len || `Require at least ${len}`,
                maxLength: len => v => (v || "").length <= len || "Too long"
                // nameRules: [
                //     v => !!v || "Name is required",
                //     v =>
                //         v.length <= 50 || "Name must be less than 50 characters"
                // ]
            }
        };
    },
    computed: {
        plantGrowthTemplates() {
            var _templates = this.plant.plantGrowthTemplateSet.edges
                ? this.plant.plantGrowthTemplateSet.edges.map(item => {
                      return {
                          node: {
                              ...item.node,
                              seasonEffectMonthList: JSON.parse(
                                  item.node.seasonEffectMonths
                              )
                          }
                      };
                  })
                : [];

            if (_templates.length > 0) {
                _templates = _templates.sort((a, b) => {
                    if (a.node.potSize.potSizeCm === b.node.potSize.potSizeCm) {
                        return a.node.name.toLowerCase() ==
                            b.node.name.toLowerCase()
                            ? 0
                            : a.node.name.toLowerCase() >
                              b.node.name.toLowerCase()
                            ? 1
                            : -1;
                    }
                    return a.node.potSize.potSizeCm - b.node.potSize.potSizeCm;
                });
            }

            return _templates;
        },
        plantProducts() {
            var _products = [];
            if (
                this.plant &&
                this.plant.productItemSet &&
                this.plant.productItemSet.edges
            ) {
                _products = [...this.plant.productItemSet.edges];
            }

            return _products;
        },
        plantPotSizes() {
            var _plantPotSizes = [];
            if (this.plant.plantPotSizeSet) {
                _plantPotSizes = [...this.plant.plantPotSizeSet.edges];

                _plantPotSizes.sort((a, b) => {
                    return a.node.potSize.potSizeCm - b.node.potSize.potSizeCm;
                });
            }

            return _plantPotSizes;
        }
    },
    watch: {},
    created() {
        this.hasPermission = helper.hasPermission;
    },
    methods: {
        // plant
        editPlant() {
            this.editPlantDialog = true;
        },

        deletePlantConfirmed() {
            if (this.plant) {
                const payload = {
                    id: this.plant.id
                };
                this.$store
                    .dispatch("plants/deletePlant", payload)
                    .then(response => {
                        // close delete dialog
                        this.deletePlantDialog = false;

                        this.$emit("item-deleted");

                        // show snackbar
                        const payload = {
                            color: "success",
                            message: `${response.name} is deleted!`
                        };
                        this.$store.dispatch("snackbar/showMessage", payload);
                    })
                    .catch(error => {
                        this.error = error.graphQLErrors[0].message;
                    });
            }
        },

        // add growth template
        addPlantGrowthTemplate(potSizeNodeId) {
            this.editedPlantGrowthTemplate = null;
            this.selectedPotSizeNodeId = potSizeNodeId;
            this.editPlantGrowthTemplateDialog = true;
        },
        plantGrowthTemplateAdded() {
            this.$apollo.queries.plant.refresh();
        },

        // copy growth template
        copyPlantGrowthTemplate(item) {
            this.isCopyGrowthTemplate = true;
            this.editedPlantGrowthTemplate = item.node;
            this.editPlantGrowthTemplateDialog = true;
        },
        plantGrowthTemplateCopied() {
            this.$apollo.queries.plant.refresh();
            this.isCopyGrowthTemplate = false;
        },

        // edit growth template
        editPlantGrowthTemplate(item) {
            this.isCopyGrowthTemplate = false;
            this.editedPlantGrowthTemplate = item.node;
            this.editPlantGrowthTemplateDialog = true;
        },
        plantGrowthTemplateEdited() {
            this.$apollo.queries.plant.refresh();
        },

        deletePlantGrowthTemplate(item) {
            this.deletedPlantGrowthTemplate = item.node;
            this.deletePlantGrowthTemplateDialog = true;
        },
        deletePlantGrowthTemplateConfirmed(item) {
            const payload = {
                id: item.id
            };

            this.$store
                .dispatch(
                    "plantGrowthTemplates/deletePlantGrowthTemplate",
                    payload
                )
                .then(response => {
                    // close delete dialog
                    this.deletePlantGrowthTemplateDialog = false;

                    this.$apollo.queries.plant.refresh();

                    // show snackbar
                    const payload = {
                        color: "success",
                        message: `${response.name} is deleted!`
                    };
                    this.$store.dispatch("snackbar/showMessage", payload);
                })
                .catch(error => {
                    this.error = error.graphQLErrors[0].message;
                });
        },

        linkPlantFixedCostTemplate(plantPotSize) {
            // ignore if plantFixedCostTemplateNodeId is empty
            if (!plantPotSize.plantFixedCostTemplateNodeId) {
                return;
            }

            // ignore if plantFixedCostTemplateNodeId is same as current
            if (
                plantPotSize.plantFixedCostTemplate &&
                plantPotSize.plantFixedCostTemplate.id ==
                    plantPotSize.plantFixedCostTemplateNodeId
            ) {
                return;
            }

            this.isLoadingTenantPlantFixedCostTemplates = true;

            this.$apollo
                .mutate({
                    // Query
                    mutation: gql`
                        mutation linkPlantPotSizeToPlantFixedCostTemplate(
                            $input: LinkPlantPotSizeToPlantFixedCostTemplateInput!
                        ) {
                            linkPlantPotSizeToPlantFixedCostTemplate(
                                input: $input
                            ) {
                                plantPotSize {
                                    id
                                    potSize {
                                        id
                                        potSizeCm
                                    }
                                    youngPlantDefaultCost
                                    plantFixedCostTemplate {
                                        id
                                        templateName
                                        totalCost
                                        plantFixedCostTemplateItemSet(
                                            orderBy: "itemName"
                                        ) {
                                            totalCount
                                            edges {
                                                node {
                                                    id
                                                    itemName
                                                    costValue
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    `,
                    // Parameters
                    variables: {
                        input: {
                            plantPotSizeNodeId: plantPotSize.id,
                            plantFixedCostTemplateNodeId:
                                plantPotSize.plantFixedCostTemplateNodeId
                        }
                    }
                })
                .then(() => {
                    // show snackbar
                    const payload = {
                        color: "success",
                        message: `Saved!`
                    };
                    this.$store.dispatch("snackbar/showMessage", payload);
                })
                .catch(error => {
                    // Error
                    console.error(error);

                    let _error = error.graphQLErrors
                        .map(error => error.message)
                        .join(", ");

                    // show snackbar
                    const payload = {
                        color: "error",
                        message: _error
                    };
                    this.$store.dispatch("snackbar/showMessage", payload);
                })
                .finally(() => {
                    this.isLoadingTenantPlantFixedCostTemplates = false;
                });
        },

        addPlantFixedCostTemplate(potSizeNodeId) {
            this.selectedPotSizeNodeId = potSizeNodeId;
            this.editPlantFixedCostTemplateDialog = true;
        },

        onPlantFixedCostTemplateCreated() {
            // refresh tenantPlantFixedCostTemplates
            this.$apollo.queries.tenantPlantFixedCostTemplates.refresh();
        },

        savePlantPotSizeYoungPlantDefaultCost(
            plantPotSizeNodeId,
            youngPlantDefaultCost
        ) {
            this.isSavingYoungPlantDefaultCost = true;

            this.$apollo
                .mutate({
                    // Query
                    mutation: gql`
                        mutation updatePlantPotSizeYoungPlantDefaultCost(
                            $input: UpdatePlantPotSizeYoungPlantDefaultCostInput!
                        ) {
                            updatePlantPotSizeYoungPlantDefaultCost(
                                input: $input
                            ) {
                                plantPotSize {
                                    id
                                    potSize {
                                        id
                                        potSizeCm
                                    }
                                    youngPlantDefaultCost
                                    plantFixedCostTemplate {
                                        id
                                        templateName
                                        totalCost
                                        plantFixedCostTemplateItemSet(
                                            orderBy: "itemName"
                                        ) {
                                            totalCount
                                            edges {
                                                node {
                                                    id
                                                    itemName
                                                    costValue
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    `,
                    // Parameters
                    variables: {
                        input: {
                            plantPotSizeNodeId: plantPotSizeNodeId,
                            youngPlantDefaultCost: youngPlantDefaultCost
                        }
                    }
                })
                .then(() => {
                    // show snackbar
                    const payload = {
                        color: "success",
                        message: `Saved!`
                    };
                    this.$store.dispatch("snackbar/showMessage", payload);
                })
                .catch(error => {
                    // Error
                    console.error(error);

                    let _error = error.graphQLErrors
                        .map(error => error.message)
                        .join(", ");

                    // show snackbar
                    const payload = {
                        color: "error",
                        message: _error
                    };
                    this.$store.dispatch("snackbar/showMessage", payload);
                })
                .finally(() => {
                    this.isSavingYoungPlantDefaultCost = false;
                });
        },

        goToProductDetail(productNodeId) {
            // this.$router.push({
            //     name: "product-detail",
            //     params: { id: productNodeId }
            // });

            let routeData = this.$router.resolve({
                name: "product-detail",
                params: { id: productNodeId }
            });
            window.open(routeData.href, "_blank");
        },

        showProductSalesPriceChart(productNodeId) {
            this.productSalesPriceChartDialog = true;

            this.$nextTick().then(() => {
                this.$refs.productSalesPriceChartRef.getProductSalesPriceByWeek(
                    productNodeId
                );
            });
        },

        closeProductSalesPriceChart() {
            this.$nextTick().then(() => {
                this.$refs.productSalesPriceChartRef.clearChartSeriesData();
            });
            this.productSalesPriceChartDialog = false;
        },

        showPlantTemplateGrowthTaskScoreListDialog(plantTemplate) {
            this.selectedPlantGrowthTemplate = plantTemplate;
            this.plantTemplateGrowthTaskScoreListDialog = true;
        }
    }
};
</script>

